import { offset, shift } from '@floating-ui/react-dom';
import { useControllableState } from '@radix-ui/react-use-controllable-state';
import { CLEAR_COLOR_NAME, isValidColor } from '@typedream/core';
import classNames from 'classnames';
import { isEqual } from 'lodash';
import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import { ChromePicker, RGBColor } from 'react-color';
import { useOutsideEvent } from '../../utils/eventAlerter';
import { Floating } from '../Floating';
import styles from './ColorPicker.module.css';

interface ColorPickerProps {
  defaultValue?: string
  value?: string
  onChange?: (val: string) => void
  children: React.ReactNode
}

interface ColorPickerContextValue {
  currentColor?: string
  selectColor: (key: string) => void
}

const ColorPickerContext = React.createContext<ColorPickerContextValue>({
  currentColor: undefined,
  selectColor: () => {
    /* do nothing */
  },
});

export function ColorPicker(props: ColorPickerProps) {
  const {
    defaultValue, value, onChange, children,
  } = props;

  const [currentColor, selectColor] = useControllableState({
    prop: value,
    defaultProp: defaultValue,
    onChange,
  });

  return (
    <ColorPickerContext.Provider
      value={useMemo(
        () => ({ currentColor, selectColor }),
        [currentColor, selectColor],
      )}
    >
      <div className={styles.colorPickerContainer}>{children}</div>
    </ColorPickerContext.Provider>
  );
}

ColorPicker.defaultProps = {
  defaultValue: undefined,
  value: undefined,
  onChange: undefined,
};

interface ColorItemProps {
  colorKey: string
  colorValue: string | undefined
}

export function ColorItem(props: ColorItemProps) {
  const { colorKey, colorValue } = props;

  const { currentColor, selectColor } = React.useContext(ColorPickerContext);

  return (
    <div
      role="none"
      onClick={() => selectColor(colorKey)}
      key={colorKey}
      className={classNames(styles.colorPickerItem, {
        [styles.isSelected]: colorKey === currentColor,
        [styles.withCrossLine]: colorKey === CLEAR_COLOR_NAME,
      })}
      style={{
        background: colorValue,
      }}
    />
  );
}

interface ColorInputProps {
  predefinedColors?: Record<string, string | undefined>
  defaultValue?: string
  value?: string // if you want it controlled
  onChange?: (color: string) => void
  onSubmit?: (color: string) => void
}

export function ColorInput(props: ColorInputProps) {
  const {
    predefinedColors = {},
    value: valueProp,
    defaultValue,
    onChange,
    onSubmit,
  } = props;
  const [controlledValue, setControlledValue] = useControllableState({
    prop: valueProp,
    defaultProp: defaultValue,
    onChange,
  });

  // we have internalValue here because it's possible that this component
  // is called with only `value` and `onSubmit` prop, without `onChange`.
  // maybe we call it semi-controlled???
  const [internalValue, setInternalValue] = useState(controlledValue);
  useEffect(() => setInternalValue(controlledValue), [controlledValue]);

  const setValue = (v: string) => {
    setInternalValue(v);
    setControlledValue(v);
  };

  const [rgba, setrgba] = React.useState({} as RGBColor);

  const handleCustomColorChange = (customColor: RGBColor) => {
    setrgba(customColor);
    if (!isEqual(customColor, {})) {
      setValue(
        `rgba(${customColor.r}, ${customColor.g}, ${customColor.b}, ${customColor.a})`,
      );
    }
  };

  const colorError = !!internalValue
    && !predefinedColors[internalValue]
    && !isValidColor(internalValue);

  const currentColor = colorError
    ? undefined
    : (internalValue && predefinedColors[internalValue]) || internalValue;

  const disabled = colorError || defaultValue === internalValue;

  const ref = useRef<HTMLDivElement>(null);

  const floatingRef = useRef<Floating>(null);

  const [isChromeColorPickerOpen, setIsChromeColorPickerOpen] = React.useState(false);

  const colorIndicatorIconRef = useOutsideEvent<HTMLButtonElement>(
    'mousedown',
    (event) => {
      if (
        floatingRef.current?.container === event.target
        || floatingRef.current?.container?.contains(event.target as Node)
      ) {
        return;
      }
      setIsChromeColorPickerOpen(false);
    },
  );

  return (
    <div className={styles.colorInputContainer} ref={ref}>
      <button
        ref={colorIndicatorIconRef}
        type="button"
        onClick={() => setIsChromeColorPickerOpen(!isChromeColorPickerOpen)}
        className={classNames(styles.colorIndicatorIcon, {
          [styles.withCrossLine]: internalValue === CLEAR_COLOR_NAME,
        })}
        style={{ background: currentColor }}
      >
        {colorError ? '?' : null}
      </button>
      <form
        className={styles.colorInputForm}
        onSubmit={(e) => {
          e.preventDefault();
          if (!disabled && internalValue) {
            onSubmit?.(internalValue);
          }
        }}
      >
        <input
          className={classNames(styles.colorInput, {
            'is-capitalized':
                internalValue && !!predefinedColors[internalValue],
          })}
          defaultValue={defaultValue}
          type="text"
          placeholder="#FFF"
          value={internalValue || ''}
          onChange={(e) => {
            let updatedValue = e.target.value;
            if (predefinedColors[updatedValue.toLowerCase()]) {
              updatedValue = updatedValue.toLowerCase();
            } else if (
              !isValidColor(updatedValue)
                && isValidColor(`#${updatedValue}`)
            ) {
              updatedValue = `#${updatedValue}`;
            }

            setValue(updatedValue);
          }}
        />
        <button
          type="submit"
          className={styles.saveButton}
          disabled={disabled}
        >
          Save
        </button>
      </form>

      <Floating
        ref={floatingRef}
        show={isChromeColorPickerOpen}
        reference={ref}
        options={{
          placement: 'left',
          strategy: 'fixed',
          middleware: [
            offset(24),
            shift({
              crossAxis: true,
              padding: { top: 8, bottom: 8 },
            }),
          ],
        }}
        autoUpdate
      >
        <ChromePicker
          onChangeComplete={({ rgb }) => handleCustomColorChange(rgb)}
          onChange={({ rgb }) => handleCustomColorChange(rgb)}
          color={rgba}
        />
      </Floating>
    </div>
  );
}

ColorInput.defaultProps = {
  predefinedColors: {},
  value: undefined,
  defaultValue: undefined,
  onChange: () => {
    /*  */
  },
  onSubmit: () => {
    /*  */
  },
};
